/**
 * useOptimisticAction Hook
 *
 * Combines action orchestrator with optimistic updates.
 * Automatically updates UI immediately, syncs with backend, and rolls back on error.
 *
 * @example
 * ```typescript
 * const { execute, isLoading } = useOptimisticAction({
 *   action: Action.UPDATE_TASK_STATUS,
 *   stateRef: boardDataRef,
 *   setState: setBoardData,
 *   optimisticUpdate: (input, currentState) => {
 *     // Return new state with card moved
 *     return moveCardInState(currentState, input.taskId, input.newStatusId);
 *   },
 * });
 *
 * // Usage: UI updates instantly, backend syncs in background
 * await execute({ taskId: '123', newStatusId: 'done' });
 * ```
 */

import { useCallback, useRef, useState, MutableRefObject } from 'react';
import { Action, ProcessorResult } from '@/business/types/action.types';
import {
  actionOrchestrator,
  ActionToastConfig,
} from '@/business/orchestrator/ActionOrchestrator';
import { ResponseCode } from '@/business/types/response-codes.types';
import { useToast } from './useToast';
import { logger } from '@/utils/logger';

/**
 * Optimistic action configuration
 */
export interface UseOptimisticActionConfig<TInput, TOutput, TState> {
  /** The action to execute */
  action: Action;

  /** Reference to current state (use ref for performance) */
  stateRef: MutableRefObject<TState | null>;

  /** State setter function */
  setState: (state: TState | null) => void;

  /**
   * Optimistic update function
   * Takes input and current state, returns new optimistic state
   */
  optimisticUpdate: (input: TInput, currentState: TState) => TState;

  /**
   * Optional: Custom rollback function
   * If not provided, will restore previous state
   */
  rollback?: (input: TInput, previousState: TState) => TState;

  /**
   * Optional: Custom toast configuration
   */
  toast?: ActionToastConfig | false;

  /**
   * Optional: Callback on success (after server confirms)
   */
  onSuccess?: (data: TOutput | undefined) => void;

  /**
   * Optional: Callback on error (after rollback)
   */
  onError?: (code: ResponseCode) => void;
}

/**
 * Return type for useOptimisticAction
 */
export interface UseOptimisticActionReturn<TInput, TOutput> {
  /** Execute the action with optimistic update */
  execute: (input: TInput) => Promise<ProcessorResult<TOutput>>;

  /** Whether action is currently executing */
  isLoading: boolean;
}

/**
 * Hook for executing actions with optimistic updates
 *
 * This hook:
 * 1. Updates UI immediately (optimistic)
 * 2. Shows loading toast
 * 3. Calls backend API
 * 4. On success: Updates toast, calls onSuccess
 * 5. On error: Rolls back UI, shows error toast, calls onError
 *
 * @template TInput Action input type
 * @template TOutput Action output type
 * @template TState Local state type
 */
export function useOptimisticAction<TInput, TOutput, TState>({
  action,
  stateRef,
  setState,
  optimisticUpdate,
  rollback,
  toast,
  onSuccess,
  onError,
}: UseOptimisticActionConfig<
  TInput,
  TOutput,
  TState
>): UseOptimisticActionReturn<TInput, TOutput> {
  const isLoadingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const previousStateRef = useRef<TState | null>(null);
  const toastMethods = useToast();

  /**
   * Execute action with optimistic update
   */
  const execute = useCallback(
    async (input: TInput): Promise<ProcessorResult<TOutput>> => {
      // Check if already loading
      if (isLoadingRef.current) {
        return {
          code: ResponseCode.OPERATION_FAILED,
          data: undefined,
        };
      }

      const currentState = stateRef.current;
      if (!currentState) {
        return {
          code: ResponseCode.INVALID_INPUT,
          data: undefined,
        };
      }

      isLoadingRef.current = true;
      setIsLoading(true);

      // Step 1: Save current state for rollback
      previousStateRef.current = currentState;

      // Step 2: Apply optimistic update (UI updates immediately)
      try {
        const newState = optimisticUpdate(input, currentState);
        setState(newState);
      } catch (error) {
        logger.error('Optimistic update failed:', error);
        isLoadingRef.current = false;
        setIsLoading(false);
        return {
          code: ResponseCode.INTERNAL_ERROR,
          data: undefined,
        };
      }

      // Step 3: Execute action via orchestrator (syncs with backend)
      const result = await actionOrchestrator.execute<TInput, TOutput>(
        action,
        input,
        { toast, toastMethods }
      );

      // Step 4: Handle result
      const isSuccess = result.code === ResponseCode.SUCCESS;

      if (isSuccess) {
        // Success - keep optimistic update
        onSuccess?.(result.data);
      } else {
        // Error - rollback to previous state
        if (previousStateRef.current) {
          if (rollback) {
            // Use custom rollback
            const rolledBackState = rollback(input, previousStateRef.current);
            setState(rolledBackState);
          } else {
            // Default: restore previous state
            setState(previousStateRef.current);
          }
        }

        onError?.(result.code);
      }

      isLoadingRef.current = false;
      setIsLoading(false);
      return result;
    },
    [
      action,
      stateRef,
      setState,
      optimisticUpdate,
      rollback,
      toast,
      toastMethods,
      onSuccess,
      onError,
    ]
  );

  return {
    execute,
    isLoading,
  };
}
